<template>
  <div class="room-extend-menu-section-contents">
    <RoomChatForm
      v-for="notice in noticeList"
      :key="notice.noticeId"
      :type="'notice'"
      :section="section"
      :item="itemObj(notice)"
      :menuList="menuList(notice)"
      @click="$emit('onClickItem', notice)"
      @onClickDownload="handleClickDownloadFile(notice)"
      @onClickDelete="onClickDelete"
      @onChangeNoticeSection="isChatSection && $emit('onChangeNoticeSection')"
    />
  </div>  
</template>

<script>

import {mapActions, mapGetters, mapMutations} from "vuex";
import RoomChatForm from '@/components/Room/Chat/RoomChatForm'
import moment from "moment";
import _ from "lodash";

export default {
  name: 'RoomNotice',
  components: {
    RoomChatForm
  },
  props: {
    section: {
      type: String,
      default: 'notice',
    },
    notice: Object,
  },
  computed: {
    ...mapGetters('notice', [
      'notices',
    ]),
    ...mapGetters('user',[
      'getRoomUser',
    ]),
    ...mapGetters('file', [
        'findByRoomFileId',
    ]),
    isChatSection() {
      return this.section == 'chat';
    },
    noticeList(){
      return !this.notice ? this.notices : [this.notice]
    },
    itemObj(){
      return notice => {
        let obj = {
          ...notice,
          receiveDate: this.getFormedDate(notice.regDate),
          content: this.getContent(notice),
          nickname: this.roomUserNickname(notice.userId),
        }
        
        if(notice.fileInfo){
          obj = {
            ...this.findByRoomFileId(notice),
            ...obj,
            receiveDate: this.getFormedDate(notice.regDate),
            content: this.getContent(notice),
            title: notice.fileInfo.filename,
            filesize: this.getFormattedSize(notice.fileInfo.filesize),
            expireDate: this.getFormedExpiredDate(notice.expireDate),
            isFile: true,
          }
        }
        
        return obj
      }
    },
    getFormattedSize() {
      return filesize => {
        let size = filesize
        if(size > 1024*1024*1024){
          return `${Math.round(filesize/(1024*1024*1024)*100)/100}GB`;
        }else if(size > 1024*1024){
          return `${Math.round(filesize/(1024*1024)*100)/100}MB`;
        }else if(size > 1024){
          return `${Math.round(filesize/1024*100)/100}KB`;
        }else{
          return `${Math.round(filesize/100)/100}Byte`;
        }
      }
    },
    getContent() {
      return notice => {
        if(notice.fileInfo?.filename){
          return notice.fileInfo.filename
        }else if(this.isChatSection){
          return notice.msg.replaceAll('\r', ' ')
        }else{
          return notice.msg
        }
      }
    },
    getFormedExpiredDate() {
      return date => {
        return !this.isUploading && moment(date).format('YYYY-MM-DD');
      }
    },
    getFormedDate() {
      return date => {
        return moment(date).format('YYYY-MM-DD HH:mm');
      }
    }, 
    menuList() {
      return notice => {
        let noticeObj = {
          noticeId: notice.noticeId,
          fixed: !notice.fixed,
        }
        let list = [];
        
        if(this.isChatSection){
          list = [{
            title: this.$t('extendMenu.notice.more'),
            icon: 'empty_note',
            click: () => {
              this.$emit('onChangeNoticeSection')
            }
          }]
        }

        if (notice.fixed) {
          return _.concat(
            list, {
            title: this.$t('extendMenu.notice.delete'),
            icon: 'arrowRightDown',
            click: () => {
              this.setNoticeFix(noticeObj)
            }
          })
        } else {
          return _.concat(
            list, {
            title: this.$t('extendMenu.notice.post'),
            icon: 'notice',
            click: () => {
              this.setNoticeFix(noticeObj)
            }
          })
        }
      }
    },
    roomUserNickname: function() {
      return userId => {
        let ru = this.getRoomUser(userId);
        if(!ru){
          return this.$t('extendMenu.notice.leavedUser') // TODO 처리 필요
        }
        return ru.nickname.escapeXml();
      }
    },
  },
  methods:{
    ...mapActions('notice', [
      'deleteNotice',
      'setNoticeFix',
    ]),
    ...mapMutations('file', [
      'UPDATE_FILE_DOWNLOAD_PROGRESS',
    ]),
    ...mapActions('file', [
      'getRoomFileUrl',
    ]),
    onClickDelete(notice){
      this.deleteNotice(notice)
    },
    async handleClickDownloadFile(roomNotice) {
      let roomFile = this.findByRoomFileId(roomNotice)
      let blobUrl = await this.getFileBlobUrl(roomFile);
      this.runUrlTag({url: blobUrl, roomFile});
    },
    async getFileBlobUrl(roomFile){
      this.UPDATE_FILE_DOWNLOAD_PROGRESS({roomFile, downloadState: 'preparing'});

      try {
        let url = await this.getRoomFileUrl(roomFile);
        let res = await fetch(url)
        if(!res.ok) {
          throw new Error(res.status)
        }

        const reader = res.body.getReader();
        const contentLength = res.headers.get('Content-Length');

        let receivedLength = 0;
        let chunks = [];
        let done = false;

        this.UPDATE_FILE_DOWNLOAD_PROGRESS({roomFile, downloadState: 'downloading'});
        while (!done) {
          const {done, value = 0} = await reader.read();

          if (done) {
            break;
          }

          chunks.push(value);
          receivedLength += value.length;

          this.UPDATE_FILE_DOWNLOAD_PROGRESS({roomFile, progress: Math.ceil(receivedLength / contentLength * 100)});
        }

        let Uint8Chunks = new Uint8Array(receivedLength), position = 0;
        for (let chunk of chunks) {
          Uint8Chunks.set(chunk, position);
          position += chunk.length;
        }

        let blobUrl = URL.createObjectURL(new Blob([Uint8Chunks]));
        return blobUrl;
      }catch (e) {
        this.$alert({
          text: this.$t('extendMenu.notice.fileServerError'),
          onlyOk: true
        });
        throw e;
      }finally {
        this.UPDATE_FILE_DOWNLOAD_PROGRESS({roomFile, downloadState: 'completed'});
      }
    },
    runUrlTag({url, roomFile}){
      const a = document.createElement('a');
      try{
        a.href = url;
        a.download = this.filenameFormat(roomFile);
        a.target= '_blank'
        document.body.append(a);
        a.click();
      }finally{
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    },
    filenameFormat(roomFile) {
      return roomFile?.fileInfo?.filename;
    },
  },
}
</script>
